/**
 * CollapsePanel — Ten → Four.
 *
 * Shows how the four conditions A1–A4 imply the ten Neutralization Theorems:
 * each condition (via its generator stratum) discharges a family of NTs. Six
 * NTs are automatic for any GL(n) element; four are contingent on the
 * conditions. Includes the CDS (CIT–SFDT equiconsistency) and FACT
 * (G1↔Lie-type) corollaries.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import type { MonographDataTableColumn } from "@csk/demo-ui";
import conditionsData from "../data/conditions.json" with { type: "json" };
interface NtRow {
  readonly nt: string;
  readonly condition: string;
  readonly generator: string;
  readonly kind: "automatic" | "contingent";
  readonly mechanism: string;
}
const NT_ROWS: readonly NtRow[] = [
  {
    nt: "PERMIT",
    condition: "A1",
    generator: "G1",
    kind: "contingent",
    mechanism: "Authority subgroup membership",
  },
  {
    nt: "COMPOSE",
    condition: "A1",
    generator: "G1",
    kind: "contingent",
    mechanism: "BCH bracket closure",
  },
  {
    nt: "SEPARATE",
    condition: "A2",
    generator: "G2",
    kind: "contingent",
    mechanism: "Block-diagonal eigenstructure (gap ≥ ε)",
  },
  {
    nt: "DATA.ORIGIN",
    condition: "A2",
    generator: "G2",
    kind: "automatic",
    mechanism: "Eigenvectors always extractable",
  },
  {
    nt: "DATA.REACH",
    condition: "A2",
    generator: "G2",
    kind: "automatic",
    mechanism: "Eigenvalues always defined",
  },
  {
    nt: "PRECOMMIT",
    condition: "A3",
    generator: "G3",
    kind: "automatic",
    mechanism: "exp(sX)·exp(tX) = exp((s+t)X) semigroup law",
  },
  {
    nt: "INV.ESTABLISH",
    condition: "A3",
    generator: "G3",
    kind: "contingent",
    mechanism: "Lyapunov equation solvability",
  },
  {
    nt: "INV.PRESERVE",
    condition: "A3",
    generator: "G3",
    kind: "automatic",
    mechanism: "exp(AXA⁻¹) = A·exp(X)·A⁻¹ conjugation invariance",
  },
  {
    nt: "RES.CONSUME",
    condition: "A4",
    generator: "G4",
    kind: "automatic",
    mechanism: "Tr(A+B) = Tr(A)+Tr(B) additivity",
  },
  {
    nt: "RES.RELEASE",
    condition: "A4",
    generator: "G4",
    kind: "automatic",
    mechanism: "det(AB) = det(A)·det(B) multiplicativity",
  },
];
const KIND_COLOR: Record<NtRow["kind"], string> = {
  automatic: panelTokens.accentCyan,
  contingent: panelTokens.accentYellow,
};
const COLUMNS: readonly MonographDataTableColumn<NtRow>[] = [
  {
    accessorKey: "nt",
    header: "Neutralization Theorem",
    cell: ({ row }) => (
      <span className="font-mono text-[12.5px] font-semibold text-[var(--mit-bright)]">
        {row.original.nt}
      </span>
    ),
  },
  {
    accessorKey: "condition",
    header: "Condition",
    cell: ({ row }) => (
      <span className="font-mono text-[12px] text-[var(--mit-a3)]">
        {row.original.condition} · {row.original.generator}
      </span>
    ),
  },
  {
    accessorKey: "kind",
    header: "Kind",
    cell: ({ row }) =>
      PanelSurface.badge(row.original.kind, KIND_COLOR[row.original.kind]),
  },
  {
    accessorKey: "mechanism",
    header: "Matrix mechanism",
    cell: ({ row }) => (
      <span className="text-[12px] text-[var(--mit-text)]">
        {row.original.mechanism}
      </span>
    ),
  },
];
export function CollapsePanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Ten → Four Collapse"
      subtitle="How the four conditions imply the ten Neutralization Theorems — six automatic for any GL(n) element, four contingent on A1–A4."
      accentColor={panelTokens.accentCyan}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          Once a system sits inside GL(n), each Neutralization Theorem becomes a
          structural property the matrix already has. Six are{" "}
          <strong className="text-[var(--mit-bright)]">automatic</strong> — they
          hold for every GL(n) element with no per-system work. Four are{" "}
          <strong className="text-[var(--mit-bright)]">contingent</strong> on
          A1–A4. Verifying four conditions discharges all ten by construction.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>A1–A4 → the ten NTs</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.DataTable
          ariaLabel="The ten Neutralization Theorems mapped to A1–A4"
          data={NT_ROWS}
          columns={COLUMNS}
          getRowId={(row) => row.nt}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>The mapping, per generator class</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>
          {conditionsData.conditions
            .map(
              (c) =>
                `${c.id} · ${c.generator} ${c.generatorName.padEnd(10)} → { ${c.neutralizes.join(", ")} }`,
            )
            .join("\n")}
        </PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Neutralization modes</PanelSurface.SectionHeading>
      <PanelSurface.Prose className="m-0 mb-3">
        The ten theorems stratify by how strongly the matrix structure
        neutralizes the class. The same four conditions place each NT in one of
        three modes — and the abstract-categorical difficulty inverts: the
        hardest operational mode (CONTAINED) is the cheapest matrix mode.
      </PanelSurface.Prose>
      <PanelSurface.FeatureGrid
        ariaLabel="Neutralization modes over GL(n)"
        variant="rows"
        items={[
          {
            id: "contained",
            eyebrow: "CONTAINED · automatic, zero cost",
            title: "Resource accounting is free",
            color: panelTokens.accentCyan,
            description:
              "RES.CONSUME and RES.RELEASE need no condition: every invertible matrix already has a trace and a determinant. They are built-in characters of GL(n), not checks to run.",
            meta: <PanelSurface.Mono>{"tr, det defined for every X ∈ GL(n)"}</PanelSurface.Mono>,
          },
          {
            id: "inert",
            eyebrow: "INERT · two conditions",
            title: "Closed under composition, stable in time",
            color: panelTokens.accentYellow,
            description:
              "COMPOSE needs A1 (BCH bracket closure of the authority block); INV.ESTABLISH and INV.PRESERVE need A3 (a Hurwitz-stable temporal generator). Deviations stay bounded and self-correct.",
            meta: <PanelSurface.Mono>{"A1 subgroup closure · A3 Re(λ) < 0"}</PanelSurface.Mono>,
          },
          {
            id: "immune",
            eyebrow: "IMMUNE · three conditions",
            title: "The class is unconstructable",
            color: panelTokens.immuneGreen,
            description:
              "PERMIT needs A1; SEPARATE, DATA.ORIGIN and DATA.REACH need A2 (a positive spectral gap); PRECOMMIT needs A3 (the exponential semigroup). These are the strongest guarantees — the violation type has no inhabitant.",
            meta: <PanelSurface.Mono>{"A1 + A2 spectral gap + A3 semigroup"}</PanelSurface.Mono>,
          },
        ]}
      />

      <PanelSurface.SectionHeading>
        Conservation laws inherited from GL(n)
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="BOP conservation is a theorem, not an axiom"
        tone="safe"
      >
        Four laws of linear algebra — proved long before CSK existed — hold for
        every matrix in GL(n). The MIT places governance inside the space where
        they apply, so resource conservation and provenance invariance hold by
        construction, exactly, with a built-in forgery detector.
      </PanelSurface.Callout>
      <PanelSurface.FeatureGrid
        ariaLabel="Conservation laws of GL(n)"
        items={[
          {
            id: "jacobi",
            eyebrow: "exp–trace–det · Jacobi",
            title: "Local rate fixes the global obligation",
            color: panelTokens.accentCyan,
            description:
              "The summed infinitesimal consumption rate (trace) determines the total obligation (determinant) exactly. Any discrepancy means the matrix was tampered with — the identity is a built-in forgery detector linking RES.CONSUME and RES.RELEASE.",
            meta: <PanelSurface.Mono>{"exp(tr X) = det(exp X)"}</PanelSurface.Mono>,
          },
          {
            id: "cyclicity",
            eyebrow: "Trace cyclicity",
            title: "Composition order is free",
            color: panelTokens.accentBlue,
            description:
              "The total resource cost does not depend on which system acts first. Governance cost is commutative by mathematical fact, not by design choice.",
            meta: <PanelSurface.Mono>{"tr(AB) = tr(BA)"}</PanelSurface.Mono>,
          },
          {
            id: "multiplicativity",
            eyebrow: "Determinant multiplicativity",
            title: "Obligations factor exactly",
            color: panelTokens.accentYellow,
            description:
              "The total obligation of a composed system is the product of the parts — no cross-system obligation leaks or is double-counted.",
            meta: <PanelSurface.Mono>{"det(AB) = det(A)·det(B)"}</PanelSurface.Mono>,
          },
          {
            id: "spectral",
            eyebrow: "Spectral invariance",
            title: "Governance meaning is intrinsic",
            color: panelTokens.immuneGreen,
            description:
              "Eigenvalues — the governance semantic content — are preserved under change of basis. The meaning does not depend on how the system is encoded; the eigenvalue signature is the conjugation-invariant semantic pin.",
            meta: <PanelSurface.Mono>{"σ(P·A·P⁻¹) = σ(A)"}</PanelSurface.Mono>,
          },
        ]}
      />

      <PanelSurface.SectionHeading>
        What lives outside the certified manifold
      </PanelSurface.SectionHeading>
      <PanelSurface.Prose className="m-0 mb-3">
        The certified region — the matrices satisfying the contingent A1, A2 and
        A3 conditions — is generic inside the stable half of GL(n,ℂ): almost
        every self-correcting system is certified. The gap is not a thin
        boundary but a fundamental partition, so whether a system is certified
        reduces to whether it self-corrects.
      </PanelSurface.Prose>
      <PanelSurface.FeatureGrid
        ariaLabel="The complement of the certified manifold"
        variant="rows"
        items={[
          {
            id: "unstable",
            eyebrow: "A3 fails · the unstable half · ≈ 50%",
            title: "Perturbations grow instead of decaying",
            color: panelTokens.accentYellow,
            description:
              "Matrices whose temporal generator has an eigenvalue with non-negative real part fill a half-space. A governance deviation amplifies rather than self-corrects — the thick, typical obstruction to certification.",
            meta: <PanelSurface.Mono>{"max Re(λ) ≥ 0"}</PanelSurface.Mono>,
          },
          {
            id: "discriminant",
            eyebrow: "A2 fails · the discriminant locus · measure zero",
            title: "Two channels become indistinguishable",
            color: panelTokens.accentBlue,
            description:
              "Matrices with repeated eigenvalues form a codimension-2 subvariety — a coincidence a system can drift into but generically avoids. When it happens the DATA theorems lose spectral separation and provenance becomes ambiguous.",
            meta: <PanelSurface.Mono>{"disc(M) = 0"}</PanelSurface.Mono>,
          },
        ]}
      />

      <PanelSurface.SectionHeading>CDS — Closed Defense Spectrum</PanelSurface.SectionHeading>
      <PanelSurface.Callout title="CIT–SFDT equiconsistency over GL(n)" tone="info">
        The four strata G1–G4 are complete and irredundant: the ten NTs are
        sufficient (CIT) and necessary (no eleventh, SFDT). No fifth stratum
        exists — gl(n) has n² degrees of freedom, all accounted for by the four
        strata, and every candidate fifth property reduces by a computable map
        to a combination of G1–G4 (Cayley–Hamilton). Adding a control outside
        the four strata is structurally incoherent.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>
        FACT — Finite Authority Classification
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout title="G1 ↔ groups of Lie type" tone="safe">
        A1's exp-stability is precisely the defining condition of a group of Lie
        type over a finite field. So the finite authority structures in any
        A1-satisfying system are exactly the groups of Lie type — the
        G1↔Lie-type arm of the CFSG bijection conjecture, now a theorem.
        Lie-type recognition algorithms (Kantor–Seress, recog) can audit a
        finite authority domain directly.
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label:
              "DifferentialSecurity monograph §11 — The Matrix Immunity Theorem (modes, conservation laws)",
            path: "theory/manuscripts/DifferentialSecurity/monograph/11_The_Matrix_Immunity_Theorem.md",
          },
          {
            label: "MATRIX-IMMUNITY-THEOREM.md §3–§6 (derivation, CDS, FACT)",
            path: "theory/structural/MATRIX-IMMUNITY-THEOREM.md",
          },
          {
            label: "RD-56 Matrix Immunity Theorem",
            path: "research/settled/RD-56-MATRIX-IMMUNITY-THEOREM.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
